import { MathTopic, QuestionCategory } from './settings';
import { ANALYSIS_QUESTIONS } from './analysisQuestions';
import { ENGINEERING_QUESTIONS } from './engineeringQuestions';
import { CALCULATION_QUESTIONS } from './calculationQuestions';
import { SCIENCE_KNOWLEDGE_QUESTIONS } from './scienceKnowledgeQuestions';
import { GENERAL_KNOWLEDGE_QUESTIONS } from './generalKnowledgeQuestions';

export interface QuestionMaterial {
  id: string;
  title: string;
  description: string;
  columns: string[];
  rows: string[][];
  note?: string;
}

export interface Question {
  id: string;
  category: Exclude<QuestionCategory, 'all'> | 'science';
  stem: string;
  options: string[];
  answer: number;
  explanation: string;
  topic?: string;
  material?: QuestionMaterial;
  retired?: boolean;
  mathTopic?: Exclude<MathTopic, 'all'>;
}

const rows: [Question['category'], string, string[], number, string][] = [
  ['math', '7 × 8 等于多少？', ['54', '56', '58', '64'], 1, '7 × 8 = 56。'],
  ['math', '一小时有多少分钟？', ['30', '60', '90', '100'], 1, '一小时等于 60 分钟。'],
  ['math', '三角形的内角和是多少度？', ['90', '120', '180', '360'], 2, '平面三角形的内角和为 180°。'],
  ['math', '0.5 与下面哪个分数相等？', ['1/4', '1/3', '1/2', '2/3'], 2, '1 ÷ 2 = 0.5。'],
  ['math', '12 个苹果平均分给 3 人，每人几个？', ['3', '4', '5', '6'], 1, '12 ÷ 3 = 4。'],
  ['math', '正方形边长为 5，周长是多少？', ['10', '15', '20', '25'], 2, '正方形周长为边长 × 4，即 20。'],
  ['math', '2、4、8、16 的下一个数是什么？', ['18', '24', '30', '32'], 3, '每个数是前一个数的两倍，16 × 2 = 32。'],
  ['math', '100 的 25% 是多少？', ['10', '20', '25', '50'], 2, '100 × 0.25 = 25。'],
  ['math', '下列哪个数是偶数？', ['13', '17', '22', '25'], 2, '偶数能够被 2 整除，22 ÷ 2 = 11。'],
  ['math', '3² 等于多少？', ['6', '8', '9', '12'], 2, '3² 表示 3 × 3，等于 9。'],
  ['science', '水的化学式是什么？', ['CO₂', 'H₂O', 'O₂', 'NaCl'], 1, '水分子含两个氢原子和一个氧原子，化学式为 H₂O。'],
  ['science', '太阳系中最大的行星是哪颗？', ['地球', '火星', '木星', '金星'], 2, '木星是太阳系中体积和质量最大的行星。'],
  ['science', '植物主要通过哪种过程利用光能制造有机物？', ['蒸发', '凝结', '光合作用', '融化'], 2, '光合作用将光能转化为化学能。'],
  ['science', '在真空中，光和声音哪个能传播？', ['只有光', '只有声音', '都能', '都不能'], 0, '声音传播需要介质，光可以在真空中传播。'],
  ['science', '磁铁的同名磁极相互靠近时会怎样？', ['相吸', '相斥', '消失', '变热'], 1, '同名磁极相斥，异名磁极相吸。'],
  ['science', '地球绕太阳公转一周大约需要多久？', ['一天', '一周', '一个月', '一年'], 3, '地球绕太阳公转一周约为一年。'],
  ['science', '温度计通常用来测量什么？', ['长度', '质量', '温度', '速度'], 2, '温度计是测量温度的仪器。'],
  ['science', '冰变成液态水的过程叫什么？', ['凝固', '熔化', '升华', '凝华'], 1, '固态变成液态称为熔化。'],
  ['science', '下列哪一种属于可再生能源？', ['煤炭', '石油', '天然气', '太阳能'], 3, '太阳能属于可再生能源。'],
  ['science', '一年四季的形成主要与什么有关？', ['地轴倾斜与公转', '月球大小', '海水盐度', '云的颜色'], 0, '地轴倾斜并随地球公转，使各地接受阳光的情况随季节变化。'],
  ['culture', '“床前明月光”的下一句是什么？', ['疑是地上霜', '春眠不觉晓', '白日依山尽', '海上生明月'], 0, '李白《静夜思》：床前明月光，疑是地上霜。'],
  ['culture', '端午节通常与哪种食物有关？', ['月饼', '汤圆', '粽子', '腊八粥'], 2, '端午节有吃粽子的传统。'],
  ['culture', '中国传统十二生肖中，羊的前一个是什么？', ['龙', '蛇', '马', '猴'], 2, '十二生肖的顺序中，午马之后是未羊。'],
  ['culture', '“画蛇添足”通常比喻什么？', ['多此一举', '坚持不懈', '互相帮助', '恰到好处'], 0, '画蛇添足比喻做了多余的事，反而不恰当。'],
  ['culture', '“千里之行”下一句是什么？', ['更上一层楼', '始于足下', '春风又绿', '学而时习之'], 1, '“千里之行，始于足下”强调从实际行动开始。'],
  ['culture', '一年有几个月？', ['10', '11', '12', '13'], 2, '公历一年共有 12 个月。'],
  ['culture', '传统中秋节在农历哪一天？', ['正月十五', '五月初五', '八月十五', '九月初九'], 2, '农历八月十五是中秋节。'],
  ['culture', '“岁寒三友”通常指松、竹和什么？', ['菊', '梅', '兰', '荷'], 1, '松、竹、梅合称岁寒三友。'],
  ['culture', '《西游记》中使用金箍棒的是谁？', ['唐僧', '沙僧', '猪八戒', '孙悟空'], 3, '孙悟空的兵器是如意金箍棒。'],
  ['culture', '中国象棋中，每方有几个“将”或“帅”？', ['1', '2', '3', '4'], 0, '每方各有一个将或帅。'],
  ['culture', '“文房四宝”指笔、墨、纸和什么？', ['印', '砚', '书', '画'], 1, '笔、墨、纸、砚合称文房四宝。'],
  ['culture', '“五岳”中的东岳是哪座山？', ['华山', '衡山', '泰山', '恒山'], 2, '东岳泰山位于山东。'],
  ['culture', '《三国演义》中，“三顾茅庐”请的是谁？', ['周瑜', '曹操', '诸葛亮', '关羽'], 2, '刘备三顾茅庐，请诸葛亮出山。'],
  ['culture', '成语“刻舟求剑”提醒人们做事应怎样？', ['只看旧经验', '越快越好', '看运气', '随情况变化调整方法'], 3, '情况变化后，原来的标记和方法可能不再适用。'],
  ['culture', '农历正月十五通常是什么节日？', ['元宵节', '端午节', '中秋节', '重阳节'], 0, '农历正月十五是元宵节。'],
  ['culture', '“清明时节雨纷纷”的作者是谁？', ['杜甫', '杜牧', '李白', '王维'], 1, '这句诗出自杜牧的《清明》。'],
  ['culture', '围棋的棋子通常有哪两种颜色？', ['红和蓝', '绿和白', '黑和白', '黄和红'], 2, '围棋使用黑、白两色棋子。'],
  ['culture', '“一日不见，如隔三秋”中的“三秋”通常形容什么？', ['天气变化', '距离很近', '秋收繁忙', '思念时感觉时间漫长'], 3, '这句话用夸张的方式形容思念之深。'],
  ['culture', '中国传统节气一共有多少个？', ['12', '24', '36', '48'], 1, '二十四节气反映一年中时令的变化。'],
  ['culture', '中国国旗上的大星旁有几颗小星？', ['2', '3', '4', '5'], 2, '五星红旗上有一颗大星和四颗小星。'],
  ['culture', '“海内存知己”的下一句是什么？', ['天涯若比邻', '独在异乡为异客', '举头望明月', '欲穷千里目'], 0, '王勃写道：海内存知己，天涯若比邻。'],
  ['culture', '茶叶、咖啡豆、稻米中，哪一种是主粮？', ['茶叶', '咖啡豆', '三种都是', '稻米'], 3, '稻米是常见的主粮。'],
  ['culture', '通常说的“江南水乡”中的“江”指哪条河？', ['黄河', '长江', '珠江', '黑龙江'], 1, '江南一般指长江以南的相关地区。'],
  ['culture', '“四大发明”不包括下面哪一项？', ['造纸术', '指南针', '蒸汽机', '印刷术'], 2, '中国古代四大发明是造纸术、指南针、火药、印刷术。'],
  ['culture', '“守株待兔”通常讽刺哪种行为？', ['勤劳耕作', '主动学习', '互相合作', '不努力而指望侥幸成功'], 3, '守株待兔比喻不主动努力，只希望获得意外收获。'],
  ['culture', '指南针指针标有 N 的一端通常指向哪个方向？', ['北', '南', '东', '西'], 0, 'N 是英文 North 的缩写，表示北方。'],
  ['culture', '一星期有多少天？', ['5', '6', '7', '8'], 2, '一星期有七天。'],
  ['culture', '地球上面积最大的海洋是哪一个？', ['印度洋', '太平洋', '大西洋', '北冰洋'], 1, '太平洋是地球上面积最大的海洋。'],
  ['culture', '骆驼常被称为什么？', ['森林之王', '草原卫士', '海中精灵', '沙漠之舟'], 3, '骆驼适应沙漠环境，因此被称为沙漠之舟。'],
  ['culture', '下列哪件物品主要用于测量长度？', ['直尺', '天平', '秒表', '量杯'], 0, '直尺用于测量长度。'],
  ['science', '下列哪种动物属于哺乳动物？', ['鲫鱼', '鲸', '青蛙', '麻雀'], 1, '鲸生活在水中，但用肺呼吸，并哺育幼崽。'],
  ['science', '月亮本身会像太阳一样发光吗？', ['会', '只在满月时会', '不会，主要反射太阳光', '只在冬天会'], 2, '我们看到的月光主要是月球反射的太阳光。'],
  ['science', '空气中含量最多的气体是什么？', ['氧气', '二氧化碳', '氢气', '氮气'], 3, '干燥空气中，氮气体积占比约为 78%。'],
  ['science', '下面哪种材料通常容易导电？', ['铜', '干木头', '橡胶', '塑料'], 0, '铜是常用的导电材料。'],
  ['science', '水蒸气遇冷变成小水滴，属于什么变化？', ['熔化', '凝固', '液化', '升华'], 2, '气态物质变成液态，称为液化。'],
  ['science', '地球自转一周大约需要多久？', ['一小时', '一天', '一月', '一年'], 1, '地球自转一周大约为一天。'],
  ['science', '蚕主要吃什么叶子？', ['松针', '荷叶', '银杏叶', '桑叶'], 3, '家蚕通常以桑叶为食。'],
  ['science', '彩虹通常与阳光通过什么有关？', ['小水滴', '石块', '沙粒', '树干'], 0, '阳光在小水滴中折射、反射并分散，形成彩虹。'],
  ['science', '食盐的主要化学成分是什么？', ['碳酸钙', '蔗糖', '氯化钠', '二氧化硅'], 2, '食盐的主要成分是氯化钠。'],
  ['science', '下面哪种动物是昆虫？', ['蜘蛛', '蚂蚁', '蜗牛', '蚯蚓'], 1, '蚂蚁是昆虫，成虫有六条腿。']
];

// Retain old IDs for an in-memory quiz during an update; newly drawn questions use the upgraded pools.
export const QUESTIONS: Question[] = [
  ...rows.map(([category, stem, options, answer, explanation], index) =>
    ({ id: 'general-' + (index + 1), category, stem, options, answer, explanation, retired: category !== 'culture' })),
  ...ANALYSIS_QUESTIONS.map(q => ({ ...q, retired: true })),
  ...ENGINEERING_QUESTIONS.map(q => ({ ...q, retired: true })),
  ...CALCULATION_QUESTIONS, ...SCIENCE_KNOWLEDGE_QUESTIONS, ...GENERAL_KNOWLEDGE_QUESTIONS
];
export const questionsIn = (category: QuestionCategory | 'science', mathTopic: MathTopic = 'all') => QUESTIONS.filter(q =>
  !q.retired && (category === 'all' || q.category === (category === 'science' ? 'culture' : category)) &&
  (q.category !== 'math' || mathTopic === 'all' || q.mathTopic === mathTopic));
